import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  CloudArrowUp, CheckCircle, XCircle, ArrowsClockwise,
  FileArrowDown, UserCircleCheck, NavigationArrow, Buildings,
  Plugs, Warning, CurrencyDollar, Upload, Envelope, PaperPlaneRight,
} from '@phosphor-icons/react';
import api from '../services/api';
import { formatDate, formatCurrency } from '../lib/utils';

import { OdmSftpTab } from '../components/integrations/OdmSftpTab';
import { EdiErrorsTab } from '../components/integrations/EdiErrorsTab';
import { RemittanceTab } from '../components/integrations/RemittanceTab';
import MigrationsCard from '../components/admin/MigrationsCard';
import { useAuth } from '../context/AuthContext';

const IntegrationsPage = () => {
  const { user: currentUser } = useAuth();
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [patients, setPatients] = useState([]);
  const [doddClaims, setDoddClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('integrations');

  // Eligibility modal
  const [eligibilityModalOpen, setEligibilityModalOpen] = useState(false);
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // SFTP state
  const [sftpStatus, setSftpStatus] = useState(null);
  const [sftpLoading, setSftpLoading] = useState(false);
  const [sftpFiles, setSftpFiles] = useState([]);

  // EDI Errors state
  const [ediErrorSummary, setEdiErrorSummary] = useState(null);
  const [ediErrors, setEdiErrors] = useState([]);
  const [ediLoading, setEdiLoading] = useState(false);

  // Remittance state
  const [remittanceHistory, setRemittanceHistory] = useState([]);
  const [remittanceLoading, setRemittanceLoading] = useState(false);

  // Email test state
  const [emailTesting, setEmailTesting] = useState(false);

  const handleSendTestEmail = async () => {
    setEmailTesting(true);
    try {
      const res = await api.post('/integrations/email/test');
      toast.success(
        `Test email sent to ${res.data.to} from ${res.data.sender}`,
        { description: `Resend id: ${res.data.email_id || 'pending'}` }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setEmailTesting(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, patientsRes, doddRes] = await Promise.all([
        api.get('/integrations/status'),
        api.get('/patients'),
        api.get('/dodd/claims', { params: { status: 'Pending' } }),
      ]);
      setIntegrationStatus(statusRes.data);
      setPatients(patientsRes.data);
      setDoddClaims(doddRes.data);
    } catch (error) {
      toast.error('Failed to load integration data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchEdiErrors = useCallback(async () => {
    setEdiLoading(true);
    try {
      const [summaryRes, errorsRes] = await Promise.all([
        api.get('/edi-errors/summary'),
        api.get('/edi-errors', { params: { limit: 50 } }),
      ]);
      setEdiErrorSummary(summaryRes.data);
      setEdiErrors(errorsRes.data.errors || []);
    } catch (error) {
      toast.error('Failed to load EDI errors');
    } finally {
      setEdiLoading(false);
    }
  }, []);

  const fetchRemittance = useCallback(async () => {
    setRemittanceLoading(true);
    try {
      const res = await api.get('/remittance/history', { params: { limit: 20 } });
      setRemittanceHistory(res.data.postings || []);
    } catch (error) {
      toast.error('Failed to load remittance history');
    } finally {
      setRemittanceLoading(false);
    }
  }, []);

  const fetchSftpStatus = useCallback(async () => {
    setSftpLoading(true);
    try {
      const res = await api.get('/odm-sftp/status');
      setSftpStatus(res.data);
    } catch (error) {
      toast.error('Failed to load SFTP status');
    } finally {
      setSftpLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'edi-errors') fetchEdiErrors();
    else if (activeTab === 'remittance') fetchRemittance();
    else if (activeTab === 'odm-sftp') fetchSftpStatus();
  }, [activeTab, fetchEdiErrors, fetchRemittance, fetchSftpStatus]);

  const handleExportEMBS = async () => {
    try {
      const response = await api.get('/integrations/embs/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `embs_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('eMBS file exported successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No claims to export');
    }
  };

  const handleVerifyEligibility = async (patient) => {
    setSelectedPatient(patient);
    setEligibilityModalOpen(true);
    setEligibilityLoading(true);
    setEligibilityResult(null);
    try {
      const response = await api.post(`/integrations/eligibility/verify-patient/${patient.id}`);
      setEligibilityResult(response.data);
    } catch (error) {
      toast.error('Eligibility check failed');
      setEligibilityResult({
        is_eligible: false, eligibility_status: 'Error',
        errors: [error.response?.data?.detail || 'Verification failed'],
      });
    } finally {
      setEligibilityLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="integrations-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Integrations</h1>
        <p className="text-slate-500 mt-1">Connect with Sandata EVV, Ohio eMBS, eligibility, EDI errors, and remittance</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="integrations" data-testid="integrations-tab"><Plugs size={16} className="mr-2" />Integrations</TabsTrigger>
          <TabsTrigger value="odm-sftp" data-testid="odm-sftp-tab"><CloudArrowUp size={16} className="mr-2" />ODM SFTP</TabsTrigger>
          <TabsTrigger value="edi-errors" data-testid="edi-errors-tab"><Warning size={16} className="mr-2" />EDI Errors</TabsTrigger>
          <TabsTrigger value="remittance" data-testid="remittance-tab"><CurrencyDollar size={16} className="mr-2" />Remittance</TabsTrigger>
        </TabsList>

        {/* Integrations Tab (status cards + eligibility) */}
        <TabsContent value="integrations" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><NavigationArrow size={18} className="text-indigo-600" />Sandata EVV</CardTitle>
                  <Badge className={integrationStatus?.sandata?.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                    {integrationStatus?.sandata?.connected ? 'Connected' : 'Pending'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-3">Electronic Visit Verification integration</p>
                <Button variant="outline" size="sm" onClick={() => window.location.href = '/evv/resolution'} data-testid="go-evv-btn">
                  Go to EVV Resolution Center
                </Button>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><Buildings size={18} className="text-indigo-600" />Ohio eMBS</CardTitle>
                  <Badge className="bg-emerald-100 text-emerald-700">Ready</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-3">{doddClaims?.length || 0} pending DODD claims</p>
                <Button variant="outline" size="sm" onClick={handleExportEMBS} data-testid="export-embs-btn">
                  <FileArrowDown size={14} className="mr-1" />Export eMBS
                </Button>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><UserCircleCheck size={18} className="text-indigo-600" />Eligibility</CardTitle>
                  <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-3">Verify patient Medicaid eligibility</p>
                <p className="text-xs text-slate-400">{patients?.length || 0} patients available</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="email-sender-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Envelope size={18} className="text-indigo-600" />
                    Email Sender
                  </CardTitle>
                  {(() => {
                    const e = integrationStatus?.email;
                    if (!e || e.status === 'not_configured') {
                      return <Badge className="bg-red-100 text-red-700">Not configured</Badge>;
                    }
                    if (e.status === 'sandbox') {
                      return <Badge className="bg-amber-100 text-amber-700">Sandbox only</Badge>;
                    }
                    return <Badge className="bg-emerald-100 text-emerald-700">Verified</Badge>;
                  })()}
                </div>
              </CardHeader>
              <CardContent>
                {integrationStatus?.email?.sender ? (
                  <>
                    <p className="text-sm text-slate-700 mb-1 font-mono truncate" title={integrationStatus.email.sender}>
                      {integrationStatus.email.sender}
                    </p>
                    {integrationStatus.email.reply_to && (
                      <p className="text-xs text-slate-400 mb-2">
                        Reply-to: {integrationStatus.email.reply_to}
                      </p>
                    )}
                    {integrationStatus.email.status === 'sandbox' && (
                      <p className="text-xs text-amber-700 mb-3 leading-snug">
                        ⚠️ Only delivers to the Resend account-owner email. Verify
                        your domain at resend.com/domains, then update SENDER_EMAIL.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-amber-700 mb-3 leading-snug">
                    RESEND_API_KEY missing — password resets and invitations
                    won't deliver until configured.
                  </p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendTestEmail}
                  disabled={emailTesting || !integrationStatus?.email?.sender}
                  data-testid="send-test-email-btn"
                >
                  <PaperPlaneRight size={14} className="mr-1" />
                  {emailTesting ? 'Sending…' : 'Send test email'}
                </Button>
              </CardContent>
            </Card>
          </div>

          <MigrationsCard currentUser={currentUser} />

          {/* Patient Eligibility Quick Check */}
          {patients.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Quick Eligibility Check</CardTitle>
                <CardDescription>Click verify on any patient to check Medicaid eligibility</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-auto">
                  {patients.slice(0, 10).map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-2 px-3 hover:bg-slate-50 rounded-lg">
                      <div>
                        <span className="font-medium text-sm">{p.first_name} {p.last_name}</span>
                        <span className="text-xs text-slate-400 ml-2 font-mono">{p.medicaid_id}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleVerifyEligibility(p)} data-testid={`verify-elig-${p.id}`}>
                        <UserCircleCheck size={14} className="mr-1" />Verify
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="odm-sftp" className="space-y-6">
          <OdmSftpTab sftpLoading={sftpLoading} sftpStatus={sftpStatus} sftpFiles={sftpFiles} onRefresh={(files) => setSftpFiles(files)} />
        </TabsContent>

        <TabsContent value="edi-errors" className="space-y-6">
          <EdiErrorsTab ediLoading={ediLoading} ediErrorSummary={ediErrorSummary} ediErrors={ediErrors} onRefresh={fetchEdiErrors} />
        </TabsContent>

        <TabsContent value="remittance" className="space-y-6">
          <RemittanceTab remittanceLoading={remittanceLoading} remittanceHistory={remittanceHistory} onRefresh={fetchRemittance} />
        </TabsContent>
      </Tabs>

      {/* Eligibility Result Modal */}
      <Dialog open={eligibilityModalOpen} onOpenChange={setEligibilityModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Eligibility Verification Result</DialogTitle>
            <DialogDescription>{selectedPatient ? `${selectedPatient.first_name} ${selectedPatient.last_name}` : ''}</DialogDescription>
          </DialogHeader>
          {eligibilityLoading ? (
            <div className="flex items-center justify-center py-8"><ArrowsClockwise size={32} className="animate-spin text-indigo-600" /></div>
          ) : eligibilityResult ? (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${eligibilityResult.is_eligible ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-3">
                  {eligibilityResult.is_eligible ? <CheckCircle size={32} className="text-emerald-600" weight="fill" /> : <XCircle size={32} className="text-red-600" weight="fill" />}
                  <div>
                    <p className={`font-bold text-lg ${eligibilityResult.is_eligible ? 'text-emerald-700' : 'text-red-700'}`}>
                      {eligibilityResult.is_eligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}
                    </p>
                    <p className="text-sm text-slate-600">{eligibilityResult.eligibility_status}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Medicaid ID</span><span className="font-mono">{eligibilityResult.medicaid_id}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Payer</span><span>{eligibilityResult.payer_name}</span></div>
                {eligibilityResult.plan_name && <div className="flex justify-between"><span className="text-slate-500">Plan</span><span>{eligibilityResult.plan_name}</span></div>}
                {eligibilityResult.effective_date && <div className="flex justify-between"><span className="text-slate-500">Effective Date</span><span>{formatDate(eligibilityResult.effective_date)}</span></div>}
                <div className="flex justify-between"><span className="text-slate-500">Verified</span><span>{formatDate(eligibilityResult.verification_date)}</span></div>
              </div>
              {eligibilityResult.errors?.length > 0 && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-semibold text-red-700 flex items-center gap-1"><Warning size={14} />Errors</p>
                  <ul className="mt-1 text-sm text-red-600 list-disc list-inside">
                    {eligibilityResult.errors.map((err, i) => <li key={`elig-err-${i}`}>{err}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter><Button variant="outline" onClick={() => setEligibilityModalOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationsPage;
